import { useEffect, useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout, TableCard } from "ui-components/Layout";
import { EditButton, CancelButton } from "ui-components/IconButton";
import { Success, Error, MessageDialog } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";

import { useCustomerValue } from "providers/CustomerContext";
import { getSOList, getSOStatusCount, deleteSO } from "services/data.outbound.service";
import SOLog from "./components/SOLog";

import { statusObj } from "layouts/views/common/enum";
import { CreateSOBackOrder } from "services/data.outbound.service";
import { useTeamValue } from "providers/TeamContext";
import { CustomDateRangePicker } from "ui-components/CustomInput";
import dayjs from "dayjs";

function SOList() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const enabled = Boolean(customerCode);
  const navigate = useNavigate();
  const location = useLocation();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("Open");
  const [tabs, setTabs] = useState([
    { label: "Open", count: 0 },
    { label: "Hold", count: 0 },
    { label: "BackOrder", count: 0 },
    { label: "Cancelled", count: 0 },
    { label: "Completed", count: 0 },
  ]);
  const date = dayjs(new Date()).format("YYYY-MM-DD");
  const sevenDaysBefore = dayjs(new Date()).subtract(1, "day").format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(sevenDaysBefore);
  const [endDate, setEndDate] = useState(date);
  const customProps = { startDate, setStartDate, endDate, setEndDate };
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);
  const addPermission =
    allowAll ||
    (Boolean(permissions["Sales Order"]) &&
      permissions["Sales Order"].findIndex((b) => b === "new") !== -1);
  const editPermission =
    allowAll ||
    (Boolean(permissions["Sales Order"]) &&
      permissions["Sales Order"].findIndex((b) => b === "edit") !== -1);
  const deactivatePermission =
    allowAll ||
    (Boolean(permissions["Sales Order"]) &&
      permissions["Sales Order"].findIndex((b) => b === "deactivate") !== -1);

  useEffect(() => {
    if (location.state) {
      setAlert(<Success content={location.state} onChange={setAlert} />);
    }
  }, [location.state]);

  useQuery({
    queryKey: ["so-list", { customerCode, status, startDate }],
    queryFn: () => getSOList({ customerCode, status: statusObj[status], orderDate: startDate }),
    enabled,
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["so-list", "status", { customerCode, status }],
    queryFn: () => getSOStatusCount({ customerCode }),
    enabled,
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deleteSO, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["so-list"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });

  const createMutation = useMutation(CreateSOBackOrder, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["so-list"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });

  const onEdit = (req) => navigate("/outbound/so/edit", { state: req });

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Delete SO Number ${req.orderNumber}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const onCreate = (req) => {
    setDialog(
      <MessageDialog
        title={`Create SO Back Order`}
        onClose={() => setDialog(null)}
        onOk={() => {
          createMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const [logRequest, setLogRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const logProp = { open, setOpen, logRequest, setLogRequest };

  const columns = [
    {
      header: "Order Number",
      accessorKey: "orderNumber",
      cell: ({ row }) => {
        const { orderId, orderNumber, customerCode } = row.original;
        return (
          <span
            style={{ color: "#1A73E8", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              setOpen(true);
              setLogRequest({ orderId, orderNumber, customerCode, status: statusObj[status] });
            }}
          >
            {orderNumber}
          </span>
        );
      },
    },
    { header: "Order Date", accessorKey: "orderDate" },
    { header: "Cust Order Num", accessorKey: "custOrdNbr" },
    { header: "Warehouse Code", accessorKey: "warehouseCode" },
    { header: "Marketplace", accessorKey: "marketplace" },
    {
      header: "Open/Allocated",
      accessorKey: "status",
      cell: ({ row }) => {
        const { status: soStatus } = row.original;
        if (soStatus === "O") {
          return "Open";
        } else if (soStatus === "A") {
          return "Allocated";
        } else {
          return "";
        }
      },
    },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { orderId, orderNumber, customerCode, status: soStatus } = row.original;
        if (status === "Open" && soStatus !== "A") {
          return (
            <>
              <EditButton
                title="Edit SO"
                disabled={!editPermission}
                onClick={() => onEdit({ orderId, orderNumber, customerCode })}
              />
              <CancelButton
                title="Cancel SO"
                disabled={!deactivatePermission}
                onClick={() => onDelete({ orderId,orderNumber })}
              />
            </>
          );
        } else if (status === "BackOrder") {
          return (
            <>
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                disabled={!addPermission}
                onClick={() => onCreate({ orderNumber })}
              >
                Create
              </MDButton>
            </>
          );
        } else {
          return <></>;
        }
      },
    },
  ];
  return (
    <ListLayout>
      <SOLog {...logProp} />

      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between">
          <MDBox display="flex" justifyContent="flex-start" margin="10px">
            <MDButton
              variant="gradient"
              color="info"
              disabled={!addPermission}
              onClick={() => navigate("/outbound/so/add")}
            >
              <Icon>add</Icon>&nbsp;so
            </MDButton>
            <MDButton
              sx={{ marginLeft: "1rem" }}
              variant="outlined"
              color="dark"
              disabled={!addPermission}
              onClick={() => navigate("/outbound/so/upload")}
            >
              <Icon>upload</Icon>&nbsp; so
            </MDButton>
          </MDBox>
          {(status === "Cancelled" || status === "Completed") && (
            <MDBox marginTop="10px">
              <CustomDateRangePicker {...customProps} />
            </MDBox>
          )}
        </MDBox>

        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default SOList;
